import React, { useEffect, useState } from 'react';
import { RefreshCw, Save, AlertCircle, ArrowRight, Users, AlertTriangle, Calculator, Check, X, Palmtree } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function PlanningGenerator() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [generatedPlanning, setGeneratedPlanning] = useState([]);
  const [employeeDaysSummary, setEmployeeDaysSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [joursParSalarie, setJoursParSalarie] = useState(4);
  
  // Statut spécial par employé (CP, absent, etc.)
  const [employeeStatus, setEmployeeStatus] = useState({});
  
  // Nombre de tournées par jour
  const [tourneesParJour, setTourneesParJour] = useState({
    dim: 37,
    lun: 37,
    mar: 47,
    mer: 46,
    jeu: 47,
    ven: 46,
    sam: 42
  });
  
  // Nombre d'astreintes par jour
  const [astreintesParJour, setAstreintesParJour] = useState({
    dim: 2,
    lun: 2,
    mar: 2,
    mer: 2,
    jeu: 2,
    ven: 2,
    sam: 2
  });

  useEffect(() => {
    fetchEmployees();
    
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = dayOfWeek === 0 ? 0 : -dayOfWeek;
    const sunday = new Date(now);
    sunday.setDate(now.getDate() + diff);
    sunday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(sunday);
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      const responsablesRes = await api.get('/employees/responsables');
      const responsableIds = responsablesRes.data.map(r => r.id);
      const chauffeurs = response.data.filter(e => !responsableIds.includes(e.id) && e.actif !== false);
      setEmployees(chauffeurs);
      
      // Initialiser le statut de chaque employé
      const initialStatus = {};
      chauffeurs.forEach(emp => {
        initialStatus[emp.id] = emp.absent_cette_semaine ? 'absent' : 'disponible';
      });
      setEmployeeStatus(initialStatus);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Erreur lors du chargement des salariés');
    } finally {
      setLoading(false);
    }
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const weekDays = getWeekDays();
  const dayKeys = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const dispoKeys = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

  // Calcul du nombre de salariés nécessaires
  const totalTournees = Object.values(tourneesParJour).reduce((a, b) => a + b, 0);
  const totalAstreintes = Object.values(astreintesParJour).reduce((a, b) => a + b, 0);
  const salariesTournees = Math.ceil(totalTournees / joursParSalarie);
  const salariesAstreintes = Math.ceil(totalAstreintes / joursParSalarie);
  const salariesNecessaires = salariesTournees + salariesAstreintes;
  
  // Compter les disponibles (pas en CP ni absent)
  const employeesDisponibles = employees.filter(e => 
    employeeStatus[e.id] === 'disponible'
  ).length;
  
  // Compter les CP
  const employeesEnCP = employees.filter(e => 
    employeeStatus[e.id] === 'cp'
  ).length;

  const updateEmployeeStatus = async (employeeId, status) => {
    setEmployeeStatus(prev => ({
      ...prev,
      [employeeId]: status
    }));
    
    // Mettre à jour en base si absent
    if (status === 'absent' || status === 'disponible') {
      try {
        await api.put(`/employees/${employeeId}`, { 
          absent_cette_semaine: status === 'absent' 
        });
      } catch (error) {
        console.error('Error updating employee status:', error);
      }
    }
  };

  const toggleDisponibilite = async (employeeId, dayKey) => {
    const employee = employees.find(e => e.id === employeeId);
    if (!employee) return;
    
    const currentDispo = employee.disponibilites?.[dayKey] !== false;
    const newDisponibilites = {
      ...employee.disponibilites,
      [dayKey]: !currentDispo
    };
    
    try {
      await api.put(`/employees/${employeeId}`, { disponibilites: newDisponibilites });
      
      setEmployees(employees.map(e => 
        e.id === employeeId 
          ? { ...e, disponibilites: newDisponibilites }
          : e
      ));
      
      toast.success(`Disponibilité ${!currentDispo ? 'activée' : 'désactivée'}`);
    } catch (error) {
      console.error('Error updating disponibilite:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const generatePlanning = async () => {
    setGenerating(true);
    try {
      const planning = [];
      const employeeDaysCount = {};
      
      employees.forEach(emp => {
        employeeDaysCount[emp.id] = 0;
      });
      
      // D'abord, ajouter les CP pour toute la semaine
      employees.forEach(emp => {
        if (employeeStatus[emp.id] === 'cp') {
          weekDays.forEach(day => {
            planning.push({
              employee_id: emp.id,
              employee_nom: `${emp.prenom} ${emp.nom}`,
              date: day.toISOString(),
              type: 'conge_paye',
              heure_debut: '',
              heure_fin: ''
            });
          });
        }
      });
      
      // Ensuite, générer les tournées et astreintes
      weekDays.forEach((day, dayIndex) => {
        const dayKey = dayKeys[dayIndex];
        const dispoKey = dispoKeys[dayIndex];
        const neededTournees = tourneesParJour[dayKey] || 0;
        const neededAstreintes = astreintesParJour[dayKey] || 0;
        
        const disponibles = employees.filter(emp => {
          const status = employeeStatus[emp.id];
          if (status !== 'disponible') return false;
          
          const dispo = emp.disponibilites?.[dispoKey] !== false;
          const underMax = employeeDaysCount[emp.id] < (emp.jours_max_semaine || joursParSalarie);
          return dispo && underMax;
        });
        
        disponibles.sort((a, b) => employeeDaysCount[a.id] - employeeDaysCount[b.id]);
        
        let assigned = 0;
        for (const emp of disponibles) {
          if (assigned >= neededTournees) break;
          
          planning.push({
            employee_id: emp.id,
            employee_nom: `${emp.prenom} ${emp.nom}`,
            date: day.toISOString(),
            type: 'travail',
            heure_debut: '08:00',
            heure_fin: '17:00'
          });
          
          employeeDaysCount[emp.id]++;
          assigned++;
        }
        
        const remainingForAstreinte = disponibles.filter(emp => 
          !planning.some(p => p.employee_id === emp.id && new Date(p.date).toDateString() === day.toDateString())
        );
        
        let astreinteAssigned = 0;
        for (const emp of remainingForAstreinte) {
          if (astreinteAssigned >= neededAstreintes) break;
          
          planning.push({
            employee_id: emp.id,
            employee_nom: `${emp.prenom} ${emp.nom}`,
            date: day.toISOString(),
            type: 'astreinte',
            heure_debut: '08:00',
            heure_fin: '20:00'
          });
          
          employeeDaysCount[emp.id]++;
          astreinteAssigned++;
        }
      });
      
      setGeneratedPlanning(planning);
      setEmployeeDaysSummary(employeeDaysCount);
      toast.success('Planning généré avec succès !');
    } catch (error) {
      console.error('Error generating planning:', error);
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const savePlanning = async () => {
    setSaving(true);
    try {
      await api.post('/planning/save', generatedPlanning);
      toast.success('Planning enregistré avec succès !');
      setTimeout(() => {
        navigate('/planning');
      }, 1000);
    } catch (error) {
      console.error('Error saving planning:', error);
      toast.error("Erreur lors de l'enregistrement du planning");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getGeneratedStats = () => {
    const stats = {};
    dayKeys.forEach((key, idx) => {
      const day = weekDays[idx];
      const dayPlannings = generatedPlanning.filter(p => 
        new Date(p.date).toDateString() === day.toDateString()
      );
      stats[key] = {
        travail: dayPlannings.filter(p => p.type === 'travail').length,
        astreinte: dayPlannings.filter(p => p.type === 'astreinte').length,
        cp: dayPlannings.filter(p => p.type === 'conge_paye').length
      };
    });
    return stats;
  };

  const generatedStats = getGeneratedStats();

  const getStatusColor = (status) => {
    switch (status) {
      case 'disponible': return 'bg-green-100 text-green-700 border-green-300';
      case 'cp': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'absent': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!hasRole(['admin', 'responsable'])) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Accès non autorisé</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planning-generator">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Générateur de Planning Chauffeurs</h2>
          <p className="text-slate-600">
            Semaine du {formatDate(weekDays[0])} au {formatDate(weekDays[6])}
          </p>
        </div>
        
        {/* Indicateurs */}
        <div className="flex items-center gap-4">
          <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 text-center">
            <div className="flex items-center gap-2 text-blue-700 mb-1">
              <Calculator size={16} />
              <span className="text-xs font-medium">Nécessaires</span>
            </div>
            <div className="text-2xl font-bold text-blue-800">{salariesNecessaires}</div>
            <div className="text-[10px] text-blue-600">
              <div>Tournées: {totalTournees}/{joursParSalarie}j = {salariesTournees}</div>
              <div>Astreintes: {totalAstreintes}/{joursParSalarie}j = {salariesAstreintes}</div>
            </div>
          </div>
          
          <div className={`rounded-lg p-3 text-center ${employeesDisponibles >= salariesNecessaires ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'}`}>
            <div className="text-xs font-medium mb-1">Disponibles</div>
            <div className={`text-2xl font-bold ${employeesDisponibles >= salariesNecessaires ? 'text-green-800' : 'text-red-800'}`}>
              {employeesDisponibles}
            </div>
            <div className="text-[10px]">
              {employeesDisponibles >= salariesNecessaires ? '✓ OK' : `Manque ${salariesNecessaires - employeesDisponibles}`}
            </div>
          </div>
          
          {employeesEnCP > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <div className="flex items-center gap-1 text-blue-600 mb-1">
                <Palmtree size={14} />
                <span className="text-xs font-medium">En CP</span>
              </div>
              <div className="text-2xl font-bold text-blue-700">{employeesEnCP}</div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          onClick={generatePlanning}
          disabled={generating}
          className="bg-brand hover:bg-brand-dark"
          data-testid="generate-planning-button"
        >
          <RefreshCw size={18} className={`mr-2 ${generating ? 'animate-spin' : ''}`} />
          {generating ? 'Génération...' : 'Générer le planning'}
        </Button>
        {generatedPlanning.length > 0 && (
          <>
            <Button onClick={savePlanning} variant="outline" disabled={saving} data-testid="save-planning-button">
              <Save size={18} className="mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Button onClick={() => navigate('/planning')} variant="outline">
              <ArrowRight size={18} className="mr-2" />
              Voir le planning
            </Button>
          </>
        )}
        <Button onClick={() => navigate('/planning/generator/responsables')} variant="outline">
          <Users size={18} className="mr-2" />
          Planning Responsables
        </Button>
      </div>

      {/* Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Tournées par jour */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Users size={18} />
            Tournées par jour
          </h3>
          <div className="grid grid-cols-7 gap-2">
            {dayNames.map((name, idx) => (
              <div key={idx} className="text-center">
                <Label className="text-xs text-slate-600">{name}</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={tourneesParJour[dayKeys[idx]]}
                  onChange={(e) => setTourneesParJour({
                    ...tourneesParJour,
                    [dayKeys[idx]]: parseInt(e.target.value) || 0
                  })}
                  className="text-center font-bold text-sm h-9"
                />
              </div>
            ))}
          </div>
          <div className="mt-2 text-right text-xs text-slate-600">
            Total: <b>{totalTournees}</b> tournées
          </div>
        </div>

        {/* Astreintes par jour */}
        <div className="bg-orange-50 rounded-lg border border-orange-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-orange-700">
            <AlertTriangle size={18} />
            Astreintes par jour
          </h3>
          <div className="grid grid-cols-7 gap-2">
            {dayNames.map((name, idx) => (
              <div key={idx} className="text-center">
                <Label className="text-xs text-orange-600">{name}</Label>
                <Input
                  type="number"
                  min="0"
                  max="20"
                  value={astreintesParJour[dayKeys[idx]]}
                  onChange={(e) => setAstreintesParJour({
                    ...astreintesParJour,
                    [dayKeys[idx]]: parseInt(e.target.value) || 0
                  })}
                  className="text-center font-bold text-sm h-9 border-orange-300"
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-between text-xs">
            <div className="flex items-center gap-2">
              <Label className="text-orange-600">Jours/salarié:</Label>
              <Input
                type="number"
                min="1"
                max="7"
                value={joursParSalarie}
                onChange={(e) => setJoursParSalarie(parseInt(e.target.value) || 4)}
                className="w-14 h-7 text-center text-sm"
              />
            </div>
            <span className="text-orange-600">Total: <b>{totalAstreintes}</b></span>
          </div>
        </div>
      </div>

      {/* Résultat de génération */}
      {generatedPlanning.length > 0 && (
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <h3 className="text-sm font-semibold mb-3 text-green-700">Résultat</h3>
          <div className="grid grid-cols-7 gap-2 text-center">
            {dayNames.map((name, idx) => {
              const key = dayKeys[idx];
              const stats = generatedStats[key];
              const needed = tourneesParJour[key];
              const isComplete = stats.travail >= needed;
              
              return (
                <div key={idx} className={`p-2 rounded-lg ${isComplete ? 'bg-green-100' : 'bg-yellow-100'}`}>
                  <p className="font-bold text-xs">{name}</p>
                  <p className="text-lg font-bold text-green-700">{stats.travail}/{needed}</p>
                  {stats.astreinte > 0 && (
                    <p className="text-[10px] text-orange-600">{stats.astreinte} astr.</p>
                  )}
                  {stats.cp > 0 && (
                    <p className="text-[10px] text-blue-600">{stats.cp} CP</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Liste des employés */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
          <h3 className="font-semibold text-sm">Chauffeurs ({employees.length})</h3>
          <p className="text-xs text-slate-600">Cliquez sur les cercles pour modifier</p>
        </div>
        
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="text-left p-2 border-b border-slate-200 min-w-[180px]">Nom</th>
                <th className="text-center p-2 border-b border-slate-200 w-24">Statut</th>
                {dayNames.map((name, idx) => (
                  <th key={idx} className="text-center p-2 border-b border-slate-200 w-12">{name}</th>
                ))}
                <th className="text-center p-2 border-b border-slate-200 w-16">Jours</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp, index) => {
                const status = employeeStatus[emp.id] || 'disponible';
                const days = employeeDaysSummary[emp.id] || 0;
                const isCP = status === 'cp';
                const isAbsent = status === 'absent';
                
                return (
                  <tr key={emp.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${isCP ? 'bg-blue-50' : ''} ${isAbsent ? 'bg-red-50 opacity-60' : ''}`}>
                    <td className="p-2 border-b border-slate-100">
                      <span className="font-medium">{emp.prenom} {emp.nom}</span>
                      {days > 0 && (
                        <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                          {days}j
                        </span>
                      )}
                    </td>
                    <td className="p-2 border-b border-slate-100 text-center">
                      <select
                        value={status}
                        onChange={(e) => updateEmployeeStatus(emp.id, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded border ${getStatusColor(status)} cursor-pointer`}
                      >
                        <option value="disponible">Disponible</option>
                        <option value="cp">En CP</option>
                        <option value="absent">Absent</option>
                      </select>
                    </td>
                    {dispoKeys.map((dispoKey, idx) => {
                      const isAvailable = emp.disponibilites?.[dispoKey] !== false;
                      return (
                        <td key={idx} className="p-2 border-b border-slate-100 text-center">
                          {isCP ? (
                            <div className="w-7 h-7 rounded-full bg-blue-400 flex items-center justify-center mx-auto">
                              <Palmtree size={14} className="text-white" />
                            </div>
                          ) : isAbsent ? (
                            <div className="w-7 h-7 rounded-full bg-red-300 flex items-center justify-center mx-auto">
                              <X size={14} className="text-white" />
                            </div>
                          ) : (
                            <button
                              onClick={() => toggleDisponibilite(emp.id, dispoKey)}
                              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                isAvailable 
                                  ? 'bg-green-500 hover:bg-green-600' 
                                  : 'bg-red-500 hover:bg-red-600'
                              }`}
                            >
                              {isAvailable ? (
                                <Check size={14} className="text-white" />
                              ) : (
                                <X size={14} className="text-white" />
                              )}
                            </button>
                          )}
                        </td>
                      );
                    })}
                    <td className="p-2 border-b border-slate-100 text-center font-medium">
                      {emp.jours_max_semaine || joursParSalarie}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
